import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { CalendarCheck, CheckCircle2, Link2, Loader2, ShieldCheck, RefreshCw } from "lucide-react";
import { BRAND } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCalendar() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/oauth/calendar/status`);
      setStatus(data);
    } catch (e) {
      setStatus({ connected: false, configured: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const params = new URLSearchParams(window.location.search);
  const justConnected = params.get("connected") === "1";
  const errored = params.get("error") === "1";

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-lg bg-[#0C142A] border border-[#1D2847] p-8 md:p-10">
        <div className="flex items-center gap-3 mb-8">
          <img src={BRAND.logo} alt="On This Rock LLC" className="h-12 w-auto" />
          <div>
            <div className="font-display font-extrabold tracking-tight text-lg leading-none">ON THIS ROCK <span className="text-[#A3A8B8] text-xs">LLC</span></div>
            <div className="text-xs uppercase tracking-wider text-[#A3A8B8] mt-1">Admin · Calendar Sync</div>
          </div>
        </div>

        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-[#D4AF37] mb-4">
          <CalendarCheck size={16} /> Google Calendar
        </div>
        <h1 className="font-display text-3xl font-black tracking-tight">Connect your booking calendar</h1>
        <p className="text-[#A3A8B8] mt-3 leading-relaxed">
          Link your Google Calendar so every "Book a Live Session" request that includes a time slot is
          automatically added to your calendar — with a Google Meet link and an email invite sent to the visitor.
        </p>

        {justConnected && (
          <div data-testid="admin-connected-banner" className="mt-6 flex items-center gap-2 bg-[#0A1124] border border-[#D4AF37]/50 px-4 py-3 text-[#F8F9FA]">
            <CheckCircle2 className="text-[#D4AF37]" size={18} /> Calendar connected successfully!
          </div>
        )}
        {errored && (
          <div className="mt-6 flex items-center gap-2 bg-[#1a0d0d] border border-[#FF4444]/50 px-4 py-3 text-[#F8F9FA]">
            Connection failed or was cancelled. Please try again.
          </div>
        )}

        <div className="mt-8">
          {loading ? (
            <div className="flex items-center gap-2 text-[#A3A8B8]"><Loader2 className="animate-spin" size={18} /> Checking status…</div>
          ) : status?.connected ? (
            <div data-testid="admin-status-connected" className="space-y-4">
              <div className="flex items-center gap-3 bg-[#0A1124] border border-[#1D2847] p-5">
                <ShieldCheck className="text-[#D4AF37]" size={24} />
                <div>
                  <div className="text-sm uppercase tracking-wider text-[#A3A8B8]">Connected as</div>
                  <div className="text-[#F8F9FA] font-semibold">{status.email || "Google account"}</div>
                </div>
              </div>
              <div className="flex gap-3">
                <a href={`${API}/oauth/calendar/login`} className="inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 font-bold uppercase tracking-wider text-sm px-5 py-3 transition-colors">
                  <RefreshCw size={16} /> Reconnect
                </a>
                <button onClick={load} className="inline-flex items-center gap-2 text-[#A3A8B8] hover:text-[#D4AF37] text-sm px-3 py-3">Refresh</button>
              </div>
            </div>
          ) : (
            <a
              href={`${API}/oauth/calendar/login`}
              data-testid="admin-connect-btn"
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#050A15] hover:bg-[#F0C84B] font-bold uppercase tracking-wider px-7 py-4 transition-colors"
            >
              <Link2 size={18} /> Connect Google Calendar
            </a>
          )}
        </div>

        <p className="mt-8 text-xs text-[#A3A8B8] leading-relaxed border-t border-[#1D2847] pt-5">
          Sign in with <span className="text-[#F8F9FA]">{BRAND.email}</span>. This page is for the business owner only.
        </p>
        <a href="/" className="mt-4 inline-block text-sm text-[#D4AF37] hover:underline">← Back to website</a>
      </div>
    </div>
  );
}
